import React, { useCallback, useEffect, useState } from 'react';
import { TrendingUp, AlertTriangle, RefreshCw, BellRing, Check, LineChart, Gauge } from 'lucide-react';
import { toast } from '../ui/sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const pct = (v) => (v === null || v === undefined ? '—' : `${(v * 100).toFixed(1)}%`);
const usd = (v) => (v === null || v === undefined ? '—' : `$${Number(v).toFixed(4)}`);

const Stat = ({ icon: Icon, label, value, helper, id }) => (
  <div className="glass-card p-5" data-testid={id}>
    <div className="flex items-center justify-between gap-4">
      <div>
        <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">{label}</p>
        <p className="mt-3 text-3xl font-bold text-text-heading">{value}</p>
        {helper && <p className="mt-2 text-xs text-text-muted">{helper}</p>}
      </div>
      <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-primary/20 bg-primary/10 text-primary"><Icon className="h-5 w-5" /></div>
    </div>
  </div>
);

export const MarketIntelPanel = ({ authFetch }) => {
  const [status, setStatus] = useState(null);
  const [margins, setMargins] = useState([]);
  const [atRisk, setAtRisk] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [briefs, setBriefs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async (notify = false) => {
    try {
      setLoading(true);
      const [s, m, a, b] = await Promise.all([
        authFetch(`${API}/market/status`),
        authFetch(`${API}/market/margins`),
        authFetch(`${API}/market/alerts?status=open`),
        authFetch(`${API}/market/intel/latest`),
      ]);
      const sj = await s.json(), mj = await m.json(), aj = await a.json(), bj = await b.json();
      setStatus(sj);
      setMargins(mj.margins || []); setAtRisk(mj.at_risk || []);
      setAlerts(aj.alerts || []);
      setBriefs(bj.briefs || []);
      if (notify) toast.success('Panel Mercado actualizado');
    } catch (e) { toast.error('No se pudo cargar el Panel Mercado'); } finally { setLoading(false); }
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  const ackAlert = async (alertId) => {
    try {
      setBusy(alertId);
      const r = await authFetch(`${API}/market/alerts/${alertId}/ack`, { method: 'POST' });
      if (!r.ok) throw new Error('No se pudo reconocer');
      toast.success('Alerta reconocida');
      load();
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };

  const st = status?.stats || {};

  return (
    <section className="space-y-6" data-testid="market-intel-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-text-muted">Fase 8 · Inteligencia de mercado</p>
          <h3 className="mt-1 text-lg font-bold text-text-heading">Panel Mercado</h3>
          <p className="mt-1 text-xs text-text-muted">Márgenes por proveedor, alertas de precio y último brief de mercado por cliente.</p>
        </div>
        <button type="button" onClick={() => load(true)} disabled={loading} data-testid="market-refresh"
          className="inline-flex items-center gap-2 rounded-full border border-border-subtle px-4 py-2 text-xs font-semibold text-text-muted transition hover:text-text-heading">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Actualizar
        </button>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <Stat icon={LineChart} label="Proveedores" value={st.providers_monitored ?? 0} helper="modelos monitoreados" id="market-stat-providers" />
        <Stat icon={BellRing} label="Alertas abiertas" value={st.open_alerts ?? 0} helper="precio / margen" id="market-stat-alerts" />
        <Stat icon={Gauge} label="Márgenes en riesgo" value={atRisk.length} helper="margen efectivo < 20%" id="market-stat-risk" />
        <Stat icon={TrendingUp} label="Briefs de mercado" value={st.briefs ?? 0} helper="generados en total" id="market-stat-briefs" />
      </div>

      {/* Márgenes por proveedor */}
      <div className="glass-card p-5" data-testid="market-margins-card">
        <h4 className="mb-4 text-sm font-semibold text-text-heading">Márgenes por proveedor</h4>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.14em] text-text-muted">
                <th className="pb-2 pr-4">Proveedor / Modelo</th>
                <th className="pb-2 pr-4">Costo</th>
                <th className="pb-2 pr-4">Precio venta</th>
                <th className="pb-2 pr-4">Margen efectivo</th>
                <th className="pb-2">Estado</th>
              </tr>
            </thead>
            <tbody data-testid="market-margins-rows">
              {margins.map((m) => (
                <tr key={m.provider_model} className="border-t border-border-subtle/60" data-testid={`market-margin-${m.provider_model}`}>
                  <td className="py-2 pr-4 font-medium text-text-heading">{m.provider_model}</td>
                  <td className="py-2 pr-4 text-text-muted">{usd(m.cost_usd)}</td>
                  <td className="py-2 pr-4 text-text-muted">{usd(m.price_usd)}</td>
                  <td className={`py-2 pr-4 font-semibold ${m.at_risk ? 'text-rose-300' : 'text-emerald-300'}`}>{pct(m.effective_margin)}</td>
                  <td className="py-2">
                    {m.at_risk
                      ? <span className="rounded-full border border-rose-400/30 bg-rose-500/10 px-2 py-0.5 text-[10px] text-rose-200">En riesgo</span>
                      : <span className="rounded-full border border-emerald-400/30 bg-emerald-500/10 px-2 py-0.5 text-[10px] text-emerald-200">Sano</span>}
                  </td>
                </tr>
              ))}
              {!margins.length && (
                <tr><td colSpan={5} className="py-4 text-text-muted" data-testid="market-margins-empty">Sin datos de márgenes todavía.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Alertas de precio */}
      <div className="glass-card p-5" data-testid="market-alerts-card">
        <h4 className="mb-4 text-sm font-semibold text-text-heading">Alertas de precio abiertas</h4>
        <div className="space-y-3" data-testid="market-alerts-list">
          {alerts.map((a) => (
            <div key={a.alert_id} className="flex flex-wrap items-start justify-between gap-3 rounded-xl border border-border-subtle bg-background/50 p-4" data-testid={`market-alert-${a.alert_id}`}>
              <div className="flex items-start gap-3">
                <AlertTriangle className="mt-0.5 h-4 w-4 text-amber-300" />
                <div>
                  <p className="text-sm font-medium text-text-heading">{a.provider_model || a.type}</p>
                  <p className="text-xs text-text-muted">{a.message || a.reason || ''} · {a.created_at ? new Date(a.created_at).toLocaleString('es-PE') : ''}</p>
                </div>
              </div>
              <button type="button" disabled={busy === a.alert_id} onClick={() => ackAlert(a.alert_id)} data-testid={`market-alert-ack-${a.alert_id}`}
                className="inline-flex items-center gap-1 rounded-full border border-border-subtle px-3 py-1.5 text-[11px] font-semibold text-text-muted transition hover:text-text-heading disabled:opacity-50">
                <Check className="h-3.5 w-3.5" /> Reconocer
              </button>
            </div>
          ))}
          {!alerts.length && <p className="text-sm text-text-muted" data-testid="market-alerts-empty">No hay alertas abiertas. Todo en orden.</p>}
        </div>
      </div>

      {/* Último brief por cliente */}
      <div className="glass-card p-5" data-testid="market-briefs-card">
        <h4 className="mb-4 text-sm font-semibold text-text-heading">Último brief de mercado por cliente</h4>
        <div className="grid gap-4 md:grid-cols-2" data-testid="market-briefs-list">
          {briefs.map((b) => (
            <div key={b.brief_id || b.client_id} className="rounded-xl border border-border-subtle bg-background/50 p-4" data-testid={`market-brief-${b.client_id}`}>
              <div className="flex items-center justify-between gap-2">
                <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">{b.client_id}</p>
                <span className="text-[10px] text-text-muted">{b.created_at ? new Date(b.created_at).toLocaleDateString('es-PE') : ''}</span>
              </div>
              <ul className="mt-3 space-y-1.5 text-xs text-text-muted">
                {(b.summary_lines || []).map((l, i) => <li key={i} className="leading-relaxed">{l}</li>)}
                {!(b.summary_lines || []).length && <li>Brief sin resumen disponible.</li>}
              </ul>
            </div>
          ))}
          {!briefs.length && <p className="text-sm text-text-muted" data-testid="market-briefs-empty">Aún no hay briefs de mercado generados.</p>}
        </div>
      </div>
    </section>
  );
};

export default MarketIntelPanel;
